import first from 'lodash/first'
import map from 'lodash/map'
import React, { FC, useMemo } from 'react'

import { Gallery } from '@Components/CMS/Gallery'
import { GalleryBlock as GraphQLGalleryBlock, GalleryBlockSlide } from '@Generated/graphql'
import { getJSONFieldValue, getLinkFieldValue } from '@Globals/CMS/FieldHelpers'

interface Slider {
  author: string
  date: Date
  headline: string
  image: string
  link: string
  linkNewTab: boolean
  rating: number
  text: string
}

interface GalleryBlockData {
  slides: Slider[]
}

const getBlockData = (galleryBlock: GraphQLGalleryBlock): GalleryBlockData => {
  const linkField = getLinkFieldValue(galleryBlock.fields || [], 'link')
  const link = linkField?.url || ''
  const linkNewTab = linkField?.newTab || false

  const firstSlide = first(galleryBlock.slides)
  const author = getJSONFieldValue<string>(firstSlide?.fields || [], 'author') || ''
  const date = new Date(getJSONFieldValue<string>(firstSlide?.fields || [], 'date') || 0)
  const headline = getJSONFieldValue<string>(firstSlide?.fields || [], 'headline') || ''
  const rating = getJSONFieldValue<number>(firstSlide?.fields || [], 'rating') || 0
  const text = getJSONFieldValue<string>(firstSlide?.fields || [], 'text') || ''

  return {
    slides: map(galleryBlock.slides || [], (x: GalleryBlockSlide): Slider => {
      const innerLinkField = getLinkFieldValue(x.fields || [], 'link')

      return {
        author: getJSONFieldValue<string>(x.fields || [], 'author') || author || '',
        date: new Date(getJSONFieldValue<string>(x.fields || [], 'date') || date || 0),
        headline: getJSONFieldValue(x.fields || [], 'headline') || headline || '',
        image: x.images?.[0]?.url || '',
        link: link || innerLinkField?.url || '',
        linkNewTab: linkNewTab || innerLinkField?.newTab || false,
        rating: getJSONFieldValue(x.fields || [], 'rating') || rating || -1,
        text: getJSONFieldValue(x.fields || [], 'text') || text || '',
      }
    }),
  }
}

interface GalleryBlockProps {
  galleryBlock: GraphQLGalleryBlock
  id?: string
}

const GalleryBlock: FC<GalleryBlockProps> = ({ galleryBlock, id }) => {
  const { slides } = useMemo(() => getBlockData(galleryBlock), [galleryBlock])

  return (
    <Gallery id={id} slides={slides} />
  )
}

export default GalleryBlock
